import { BadgeVariant, StatusMeta } from '../status/status-meta.types';

/**
 * The entity-type vocabulary — the frontend's single source, mirroring
 * `Entity.TYPE_CHOICES` in `backend/flow_ai/entities/models.py`. Adding a type
 * is one edit here; `backend/tests/entities/test_entity_type_vocabulary.py`
 * fails if the two lists drift.
 *
 * Order is the dropdown order. Dropdowns iterate this array; label lookups go
 * through `ENTITY_TYPE_LABELS` below.
 */
export const ENTITY_TYPE_OPTIONS = [
  { value: 'PTY', label: 'Pty Ltd' },
  { value: 'CC', label: 'Closed Corporation' },
  { value: 'INC', label: 'Incorporated Practice (Inc)' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'NPO', label: 'NPO' },
  { value: 'SOLE_PROP', label: 'Sole Proprietor' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'OTHER', label: 'Other' },
] as const satisfies readonly { value: string; label: string }[];

export type EntityType = (typeof ENTITY_TYPE_OPTIONS)[number]['value'];

/**
 * Value → label, derived so it cannot fall behind the list above. Keyed by
 * `string` on purpose: callers pass a server-supplied `type` and fall back to
 * the raw code, so a type the frontend has not heard of renders as its code
 * rather than an empty cell.
 */
export const ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  ENTITY_TYPE_OPTIONS.map((t) => [t.value, t.label]),
);

type EntityStatus = 'pending_activation' | 'active' | 'inactive';

export const ENTITY_STATUS_META: Record<EntityStatus, StatusMeta> = {
  active: { label: 'Active', variant: 'success' as BadgeVariant },
  pending_activation: { label: 'Pending activation', variant: 'warning' as BadgeVariant },
  inactive: { label: 'Inactive', variant: 'neutral' as BadgeVariant },
};
export type ContextType = 'entity' | 'agent' | 'system';
export type EntityRole = 'entity-admin' | 'entity-member' | 'agent';

export interface Context {
  type: ContextType;
  uuid: string | null;
  label: string;
  role: string;
  url: string;
  // Enrichment fields — only present on `entity` contexts.
  entity_type?: EntityType;
  entity_type_display?: string;
  industry?: string | null;
  status?: EntityStatus;
  status_display?: string;
  trading_since?: string | null;
  // Live funding signal for the card eyebrow — only present on `entity`
  // contexts that have a live application or active facility; absent otherwise.
  funding?: ContextFunding | null;
  // Ops tier — only present on `system` contexts for ops staff.
  ops_tier?: 'ANALYST' | 'SENIOR_OPS' | 'CREDIT_COMMITTEE' | null;
  // Whether this Ops user sits on the Credit Committee (may cast a resolution
  // approval). Orthogonal to ops_tier — the "dual hat". Present on `system`.
  is_credit_committee_member?: boolean;
  // Agency membership — only present on an `agent` context when the agent acts
  // as part of an agency (rather than as an individual). Drives the agency name
  // in the dashboard header and the Team panel's admin actions.
  agency?: AgentAgencyContext;
}

/** The agency an agent belongs to, carried on their `agent` context. */
interface AgentAgencyContext {
  short_id: string;
  name: string;
  member_role: 'ADMIN' | 'AGENT';
}

/** One salient line of funding status shown on a business card. */
interface ContextFunding {
  /** Human product name, e.g. "Kickstart Cash" or "Debtors Finance". */
  product: string;
  /** Short status, e.g. "More info needed", "Under review", "Active facility". */
  label: string;
  /** Urgency tone — drives the eyebrow colour and urgency sort. */
  tone: 'attention' | 'neutral' | 'positive';
  /** True when the owner must act (MORE_INFO_REQUIRED). */
  needs_attention: boolean;
}

export interface ContextsResponse {
  contexts: Context[];
}

export interface AgentEntitiesResponse {
  entities: Entity[];
}

export interface Entity {
  id: string;
  short_id: string;
  name: string;
  trading_name: string;
  reg_no: string;
  type: EntityType;
  industry: string;
  trading_start_month: number;
  trading_start_year: number;
  status: EntityStatus;
  created_at: string;
}

/**
 * Whether the client may re-elect into Debtors Finance (Constitution §A2.3),
 * and what they are told when they may not.
 *
 * Client-safe by construction: a bool, a code and one sentence — the server
 * evaluates the rule's five conditions and never sends the signals behind them
 * (N4). `stale_bank_data` rides alongside rather than inside `available`
 * because §A2.3 says stale data leaves the path open and surfaces a refresh as
 * an action: "the intent is not blocked".
 */
interface DfReElectionState {
  available: boolean;
  already_elected: boolean;
  stale_bank_data: boolean;
  reason_code: string;
  reason: string;
}

export interface EntityDetail extends Entity {
  your_role: EntityRole;
  /** The canonical DF_INTENT on the business (BRE-SCH-001). */
  df_intent: boolean;
  df_re_election: DfReElectionState;
}

export interface DeletedEntity extends Entity {
  deleted_at: string;
}

export interface DeletedEntitiesResponse {
  entities: DeletedEntity[];
}

/** A registry source's record for a number (`entities/company_lookup.py`). `UNAVAILABLE` stops nobody. */
/** The name a form may show (DOC-93 §1): nothing else leaves the server for a client. */
export interface CompanyNameLookup {
  reg_no: string;
  outcome: 'FOUND' | 'UNAVAILABLE';
  registered_name?: string;
}

/** Everything a registry source said, for Ops only (identity numbers among it). */
export interface CompanyLookup {
  reg_no: string;
  outcome: 'FOUND' | 'NOT_FOUND' | 'ERROR';
  source_label?: string;
  registered_name?: string;
  type_description?: string;
  status_description?: string;
  registration_date?: string | null;
  business_start_date?: string | null;
  industry?: string;
  financial_year_end?: string;
  physical_address?: string;
  postal_address?: string;
  looked_up_at?: string;
}

export interface CheckRegNoResponse {
  available: boolean;
  code?: string;
  detail?: string;
}

export interface CreateEntityPayload {
  name: string;
  trading_name?: string;
  reg_no: string;
  type: EntityType;
  industry: string;
  trading_start_month: number;
  trading_start_year: number;
  role: 'entity-admin';
  company_name_confirmed?: boolean;
  preparer_attestation: {
    consent_text_version: string;
    attested_at: string;
  };
}

/**
 * Payload for an agent onboarding a client business on the client's behalf.
 * The agent is linked as the AgentEntity; the named responsible person becomes
 * the entity-admin and is invited to set their own password.
 */
export interface AgentOnboardClientPayload {
  company_name_confirmed?: boolean;
  name: string;
  trading_name?: string;
  reg_no: string;
  type: EntityType;
  industry: string;
  trading_start_month: number;
  trading_start_year: number;
  client_first_name: string;
  client_last_name: string;
  client_email: string;
  preparer_attestation: {
    consent_text_version: string;
    attested_at: string;
  };
}
